import React, { useRef, useEffect, useState } from "react";
import { Modal, Button, Tooltip, Dropdown, Space } from "antd";
import "tui-image-editor/dist/tui-image-editor.css";
import "tui-color-picker/dist/tui-color-picker.css";
import ImageEditorCore from "tui-image-editor";

const ImageEditorModal = ({ visible, image, onSave, onCancel }) => {
    const editorRef = useRef(null);
    const containerId = "tui-container";
    const [currentTool, setCurrentTool] = useState('crop');
    const [selectedColor, setSelectedColor] = useState('#ff0000');

    useEffect(() => {
        if (!visible || !image?.url) return;

        const timer = setTimeout(() => {
            const container = document.getElementById(containerId);
            if (!container) return;

            // Destroy existing editor
            if (editorRef.current) {
                try {
                    editorRef.current.destroy();
                } catch (error) {
                    console.warn("Error destroying editor:", error);
                }
                editorRef.current = null;
            }

            try {
                editorRef.current = new ImageEditorCore(container, {
                    includeUI: {
                        loadImage: {
                            path: image.url,
                            name: image.name || "Image",
                            crossOrigin: "anonymous",
                        },
                        menu: ["crop", "draw", "shape", "text"],
                        initMenu: "crop",
                        menuBarPosition: "bottom",
                        theme: {
                            "header.backgroundColor": "transparent",
                            "header.border": "0px",
                            "header.display": "none",
                        },
                    },
                    cssMaxWidth: window.innerWidth - 100,
                    cssMaxHeight: window.innerHeight - 200,
                    usageStatistics: false,
                });

                // Ensure UI is properly initialized
                if (editorRef.current.ui) {
                    editorRef.current.ui.resizeEditor();
                }

                // Add event listeners to track current tool
                if (editorRef.current.ui && editorRef.current.ui.on) {
                    editorRef.current.ui.on('modeChange', (mode) => {
                        setCurrentTool(mode);
                    });
                }
            } catch (error) {
                console.error("Error initializing image editor:", error);
            }
        }, 100);

        return () => {
            clearTimeout(timer);
            if (editorRef.current) {
                try {
                    editorRef.current.destroy();
                } catch (error) {
                    console.warn("Error destroying editor on cleanup:", error);
                }
                editorRef.current = null;
            }
        };
    }, [visible, image?.url]);

    const getRandomPosition = () => ({
        left: Math.random() * 200 + 50,
        top: Math.random() * 200 + 50
    });

    const addShape = (shapeType, customOptions = {}) => {
        if (!editorRef.current) return;

        // First activate shape mode
        try {
            if (editorRef.current.ui && editorRef.current.ui.changeMenu) {
                editorRef.current.ui.changeMenu('shape');
            }
        } catch (error) {
            console.warn("Could not change to shape menu:", error);
        }

        const position = getRandomPosition();
        const baseOptions = {
            fill: 'transparent',
            stroke: selectedColor,
            strokeWidth: 3,
            ...position
        };

        try {
            let shapeOptions;

            switch (shapeType) {
                case 'rect':
                    shapeOptions = {
                        ...baseOptions,
                        width: 120,
                        height: 80,
                        ...customOptions
                    };
                    break;
                case 'circle':
                    shapeOptions = {
                        ...baseOptions,
                        radius: 60,
                        ...customOptions
                    };
                    break;
                case 'triangle':
                    shapeOptions = {
                        ...baseOptions,
                        width: 100,
                        height: 100,
                        ...customOptions
                    };
                    break;
                default:
                    shapeOptions = { ...baseOptions, ...customOptions };
            }

            editorRef.current.addShape(shapeType, shapeOptions);
            setCurrentTool('shape');
        } catch (error) {
            console.error(`Error adding ${shapeType}:`, error);
        }
    };

    // Text mode activation
    const activateTextMode = () => {
        if (!editorRef.current || !editorRef.current.ui) return;

        try {
            // Switch to text mode explicitly
            editorRef.current.ui.changeMenu('text');
            setCurrentTool('text');
        } catch (error) {
            console.error("Error activating text mode:", error);
        }
    };

    // Drawing mode activation
    const activateDrawMode = () => {
        if (!editorRef.current || !editorRef.current.ui) return;

        try {
            editorRef.current.ui.changeMenu('draw');
            setCurrentTool('draw');
        } catch (error) {
            console.error("Error activating draw mode:", error);
        }
    };

    // Crop mode activation
    const activateCropMode = () => {
        if (!editorRef.current || !editorRef.current.ui) return;

        try {
            editorRef.current.ui.changeMenu('crop');
            setCurrentTool('crop');
        } catch (error) {
            console.error("Error activating crop mode:", error);
        }
    };


    const handleSave = async () => {
        if (!editorRef.current) {
            console.warn("Editor not initialized");
            return;
        }

        try {
            const dataURL = editorRef.current.toDataURL();

            if (!dataURL || dataURL === 'data:,') {
                throw new Error("Failed to generate image data");
            }

            const blob = await (await fetch(dataURL)).blob();

            const fileName = `edited_${image.name || 'image'}.png`;
            const file = new File([blob], fileName, { type: "image/png" });

            onSave(file, image.id, image.moduleKey || 'general');
        } catch (error) {
            console.error("Error saving edited image:", error);
        }
    };

    const handleCancel = () => {
        if (editorRef.current) {
            try {
                editorRef.current.destroy();
            } catch (error) {
                console.warn("Error destroying editor on cancel:", error);
            }
            editorRef.current = null;
        }
        onCancel();
    };

    return (
        <Modal
            title={
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span>{`Edit Image: ${image?.name || "Unknown"}`}</span>

                </div>
            }
            open={visible}
            onCancel={handleCancel}
            width="90vw"
            style={{ top: 20 }}
            destroyOnClose={true}
            footer={[
                <Button key="cancel" onClick={handleCancel}>
                    Cancel
                </Button>,
                <Button key="save" type="primary" onClick={handleSave}>
                    Save
                </Button>,
            ]}
        >
            <div
                id={containerId}
                style={{
                    height: "100% !important",
                    minHeight: 500,
                    backgroundColor: "#fff",
                    border: "1px solid #d9d9d9",
                    borderRadius: "6px",
                }}
            />
        </Modal>
    );
};

export default ImageEditorModal;